import { useCallback, useEffect, useRef, useState } from 'react';

// iframe + slide-sync glue against the studio preview proxy.
//
// The deck shell (shared/src/shell/useSlideNav.js) already syncs presenter
// windows over a BroadcastChannel named 'deck-nav' ({type:'goto', idx} /
// {type:'request-sync'}). The proxied preview iframe is same-origin with the
// studio client, so the editor speaks that protocol directly: post 'goto' to
// navigate the deck, listen for the deck's own 'goto' publications to follow
// keyboard/scroll navigation inside the frame. No deck-side changes needed.
//
// DOUBLE-BUFFERED (2026-08-01). Every preview reload — an apply, a deck
// mutation, and above all flipping Before/After on a proposal — used to blank
// this pane to "Starting preview…" for a second or two while the deck's whole
// app booted, and the frame that came back often showed slide 1 (or whatever
// slide the boot published) before snapping to the right one. Comparing two
// versions through that is the one thing the flip exists to do.
//
// So there are TWO iframes. A reload loads into the hidden one; the visible
// one keeps showing the current slide throughout. Only when the new frame has
// loaded AND been put on the right slide do they swap, in a hard cut — no
// crossfade, because a fade between two nearly identical slides reads as
// exactly the flicker this removes. The retired frame is then blanked, so two
// live decks overlap for one frame, never longer.
//
// Why not HMR: the websocket IS proxied (routes/proxy.ts) and the deck runs
// @vitejs/plugin-react, but a slide module's `export const meta` is a
// non-component export, which makes it Fast-Refresh-ineligible — so Vite
// answers a slide edit with a full page reload anyway. The reload is not
// avoidable from here; showing it to the user is.
//
// …and that reload had to be TAKEN OFF the visible frame as well (2026-08-01),
// or the double buffer protects nothing: Vite sends `full-reload` to every
// connected client, so the front buffer navigated itself — black, then the
// deck's cover, then back — every time a slide file was written. Flipping
// Before/After writes it twice. `hmr={false}` (the default) puts `__hmr=0` in
// the URL, and the proxy disarms Vite's full-reload for that document; the
// reloads below become the only ones. See process/hmr-guard.ts.
//
// A frame that does NOT bump `reloadKey` on every change it wants to see —
// the new-deck build preview, which only knows the slide COUNT — passes
// `hmr` and keeps Vite's reload.

const SLOTS = [0, 1];

// FIRST BOOT NEEDS A WATCHDOG (restored 2026-08-03). cfa30f8 added one; the
// double-buffer rewrite (e5f2804) moved every timer inside the reload effect,
// which bails on `!bootedRef.current` — so the first load, the one with nothing
// on screen behind it, was the only load with no recovery at all. A deck whose
// Vite child is cold (a just-booted server, a container waking up) can exceed
// the pool's 25s READY_TIMEOUT_MS; `ensureVite` then throws and the proxy
// answers 502 as text/plain. `onLoad` fires for that, so it used to count as a
// successful boot: skeleton gone, no error, black pane forever.
const BOOT_RETRY_MS = 10_000;
// Past the pool's own 25s ready budget, so a slow-but-working cold start is
// never declared dead before the server has finished trying.
const BOOT_FAIL_MS = 35_000;
// After the document loads, the deck's JS gets this long to mount into #root.
const MOUNT_GRACE_MS = 8_000;

/**
 * A load that isn't a deck. Same-origin, so the document is readable: the
 * proxy's failures (`Could not start preview…`, `Preview upstream failed…`)
 * come back as text/plain, which is unambiguous. Returns the message to show,
 * or null when the frame looks fine / can't be judged.
 */
function frameFailure(el: HTMLIFrameElement | null): string | null {
  try {
    const doc = el?.contentDocument;
    if (!doc || !doc.contentType) return null;
    if (doc.contentType.includes('html')) return null;
    const text = (doc.body?.innerText || '').trim();
    return text ? text.slice(0, 300) : 'The preview server returned an error.';
  } catch {
    return null; // unreadable → don't guess
  }
}

/**
 * Did the deck actually mount? A module-level failure (an unresolvable
 * `@assets/…` import, two React copies) stops `createRoot().render` ever
 * running — no ErrorBoundary is mounted to catch it, so #root stays empty and
 * the frame paints the deck's flat `html,body` grey. Indistinguishable from a
 * loading pane until someone asks.
 */
function frameMounted(el: HTMLIFrameElement | null): boolean {
  try {
    const doc = el?.contentDocument;
    if (!doc) return true;
    const root = doc.getElementById('root');
    if (!root) return true; // about:blank or a non-deck document — not ours to judge
    return root.children.length > 0;
  } catch {
    return true;
  }
}

type PreviewFrameProps = {
  slug: string;
  activeIdx: number;
  /** The deck navigated itself. RETURN a different index to redirect it —
   *  that is how the library item editor clamps navigation to its own slides;
   *  the correction is posted straight back and the deck's echo swallowed. */
  onActiveChange?: (idx: number) => number | void;
  /** Bumped by any studio path that changes a slide, to force a reload. */
  reloadKey?: number;
  /** Keep Vite's own reload on this frame. Only for a surface that CANNOT
   *  promise to bump reloadKey — see studio/CLAUDE.md on the HMR guard. */
  hmr?: boolean;
  /** Render the deck's per-slide chrome — the logo, the section label and the
   *  slide number. Off for a library item: a shelf slide is not page 3 of
   *  anything, and the label it would carry names the deck it happens to sit
   *  in rather than the one it is destined for. The thumbnail pipeline and the
   *  zoom modal have always passed `?chrome=0` for the same reason. */
  chrome?: boolean;
  /** This surface will ACT on a geometry badge click — it registers
   *  `useGeometryBadgeFix` and has somewhere to put the fix.
   *
   *  Opt-in, because the badge's "click to fix" is a promise. It used to be
   *  offered on every embed (the badge inferred it from being in an iframe),
   *  including the two surfaces with no composer, where the click posted a
   *  message nobody heard. A surface that forgets this flag gets a badge that
   *  still reports the problem and does not offer a fix. */
  canFix?: boolean;
};

export default function PreviewFrame({
  slug,
  activeIdx,
  onActiveChange,
  reloadKey = 0,
  hmr = false,
  chrome = true,
  canFix = false,
}: PreviewFrameProps) {
  const frames = useRef<(HTMLIFrameElement | null)[]>([null, null]);
  const [front, setFront] = useState(0);
  const frontRef = useRef(0);
  frontRef.current = front;
  // First paint only: there is no previous frame to hold, so this is the one
  // time a skeleton is honest.
  const [booted, setBooted] = useState(false);
  const bootedRef = useRef(false);
  bootedRef.current = booted;
  // A reload is in flight in the back buffer. Drives the "Updating…" chip —
  // the frame on screen is real but one version behind, and silence there
  // would read as "nothing happened".
  const [updating, setUpdating] = useState(false);
  const swapPending = useRef(false);
  // The 14s swap watchdog gives up on the CHIP, but a back buffer that loads
  // late still holds newer content than the frame on screen — so record that a
  // late arrival is still wanted rather than rejecting it (it used to be
  // dropped by the `!swapPending.current` guard and never revealed).
  const lateSwap = useRef(false);
  // A load that produced no deck. Null while things are fine.
  const [error, setError] = useState<string | null>(null);
  // Bumped by Retry to re-aim the front buffer and re-arm the boot watchdog.
  const [bootNonce, setBootNonce] = useState(0);

  const channelRef = useRef<BroadcastChannel | null>(null);
  // Last idx exchanged on the channel (either direction) — stops the
  // editor echoing the deck's own navigation straight back at it.
  const syncedIdx = useRef<number | null>(null);
  const reloadGuardUntil = useRef(0);
  const activeIdxRef = useRef(activeIdx);
  activeIdxRef.current = activeIdx;
  const onActiveChangeRef = useRef(onActiveChange);
  onActiveChangeRef.current = onActiveChange;
  const lastReloadKey = useRef(reloadKey);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const baseSrc =
    `/preview/${slug}/?__studio=1${hmr ? '' : '&__hmr=0'}` + (canFix ? '&__fix=1' : '') + (chrome ? '' : '&chrome=0');
  const urlFor = (key?: number | string) => `${baseSrc}${key ? '&_r=' + key : ''}#${activeIdxRef.current + 1}`;

  const clearTimers = () => {
    for (const t of timers.current) clearTimeout(t);
    timers.current = [];
  };
  const later = (fn: () => void, ms: number) => {
    const t = setTimeout(fn, ms);
    timers.current.push(t);
    return t;
  };

  // Put the deck on the active slide. Both frames share one channel — they are
  // the same deck on the same slide, so the visible one treats it as a no-op.
  const assertPosition = useCallback(() => {
    if (activeIdxRef.current <= 0) return;
    syncedIdx.current = activeIdxRef.current;
    channelRef.current?.postMessage({ type: 'goto', idx: activeIdxRef.current });
  }, []);

  // First load: slot 0 boots with the active slide in the hash. Imperative
  // rather than a React `src` prop, because the reload path needs to aim a
  // specific URL at a specific slot without a re-render fighting it.
  //
  // Its watchdog is separate from `timers` on purpose: `clearTimers()` belongs
  // to the reload path, and a reload arriving mid-boot must not disarm the only
  // thing that can rescue a boot that never lands.
  useEffect(() => {
    const el = frames.current[frontRef.current] || frames.current[0];
    if (!el) return;
    if (!el.src || bootNonce > 0) el.src = urlFor(bootNonce ? `boot-${bootNonce}` : 0);

    const bootTimers: ReturnType<typeof setTimeout>[] = [];
    // Re-aim once. A child that lost a spawn race, or was evicted between the
    // document request and now, generally answers the second attempt.
    bootTimers.push(
      setTimeout(() => {
        if (bootedRef.current) return;
        const cur = frames.current[frontRef.current];
        if (cur) cur.src = urlFor(`boot-retry-${bootNonce}`);
      }, BOOT_RETRY_MS),
    );
    bootTimers.push(
      setTimeout(() => {
        if (bootedRef.current) return;
        setError('The preview did not start. The deck server may still be waking up.');
      }, BOOT_FAIL_MS),
    );
    return () => bootTimers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, bootNonce]);

  // Claim this deck's Vite child for as long as a frame is pointed at it, so
  // warming other decks (gallery, Library, Dashboard) can't LRU-evict the one
  // on screen. Best-effort: a missed release is collected by the pool's idle
  // reaper, so failures here are deliberately silent.
  useEffect(() => {
    if (!slug) return;
    const opts: RequestInit = { method: 'POST', credentials: 'same-origin' };
    fetch(`/api/preview/${encodeURIComponent(slug)}/claim`, opts).catch(() => {});
    return () => {
      fetch(`/api/preview/${encodeURIComponent(slug)}/release`, opts).catch(() => {});
    };
  }, [slug]);

  const retry = useCallback(() => {
    clearTimers();
    setError(null);
    setBooted(false);
    bootedRef.current = false;
    swapPending.current = false;
    lateSwap.current = false;
    setUpdating(false);
    setBootNonce((n) => n + 1);
  }, []);

  // HMR can't help (see the note at the top), so a stale preview means a real
  // reload — but into the BACK buffer, where nobody has to watch it.
  //
  // Still deferred: the signal fires the moment the slide file is written, and
  // loading before the Vite child's watcher has invalidated the module would
  // serve the stale transform — the user would see the original and conclude
  // preview/apply did nothing.
  useEffect(() => {
    if (lastReloadKey.current === reloadKey) return;
    // NOT booted: do not mark the key consumed. The old code recorded it and
    // then bailed, on the reasoning that a first boot "will load fresh anyway"
    // — untrue when the write lands after the frame's own HTTP fetch. With
    // `__hmr=0` suppressing Vite's full-reload, reloadKey is the ONLY reload
    // channel, so the edit stayed invisible until something else bumped it.
    // `booted` is in the dep list, so this replays the moment boot completes.
    if (!bootedRef.current) return;
    lastReloadKey.current = reloadKey;
    const back = frames.current[1 - frontRef.current];
    if (!back) return;

    clearTimers();
    setUpdating(true);
    swapPending.current = true;
    lateSwap.current = false;
    later(() => {
      reloadGuardUntil.current = Date.now() + 600;
      back.src = urlFor(reloadKey);
    }, 450);

    // Watchdog: a swallowed load or a wedged Vite child must not strand the
    // user on a frame that is quietly one version out of date. Re-aim once,
    // then give up on the chip so the pane stops claiming to be working.
    later(() => {
      const back = frames.current[1 - frontRef.current];
      if (swapPending.current && back) back.src = urlFor(reloadKey + '-retry');
    }, 6000);
    later(() => {
      if (swapPending.current) {
        // Stop claiming to be working, and release the channel guard — but
        // remember that the back buffer is still the newer content, so a load
        // that arrives after this is swapped in instead of being discarded.
        swapPending.current = false;
        lateSwap.current = true;
        setUpdating(false);
      }
    }, 14000);
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, baseSrc, booted]);

  useEffect(() => clearTimers, []);

  // Deck → editor: follow navigation happening inside the iframe. The channel
  // is scoped to the deck's preview path (matching useSlideNav.js) so two
  // editor tabs on different decks — or gallery/workbench viewers — never
  // cross-navigate each other.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel(`deck-nav::/preview/${slug}/`);
    channelRef.current = ch;
    ch.onmessage = (e) => {
      const msg = e?.data;
      if (!msg || msg.type !== 'goto' || typeof msg.idx !== 'number') return;
      // A frame booting in the BACK buffer publishes its own position on the
      // way up. That is not the user navigating — and following it is exactly
      // how a flip used to dump the editor on a different slide.
      if (swapPending.current) return;
      // Ignore the idx=0 a freshly (re)loaded iframe publishes before its
      // hash-restore runs. Without this, a reload would snap the editor
      // back to slide 1.
      if (msg.idx === 0 && activeIdxRef.current !== 0 && Date.now() < reloadGuardUntil.current) {
        return;
      }
      // A goto for the slide we're already on is an echo (the hash-restore a
      // reloaded iframe publishes, or the deck confirming our own postMessage)
      // — not user navigation. Reporting it upward would re-engage slide
      // scope (Canvas selectSlide), snapping the user out of deck scope every
      // time a preview/apply reloads the frame.
      if (msg.idx === activeIdxRef.current) {
        syncedIdx.current = msg.idx;
        return;
      }
      syncedIdx.current = msg.idx;
      // The handler may return a different idx to redirect to — the library
      // item editor clamps navigation to its item's slides this way. Post the
      // correction straight back; the deck's echo of it is swallowed above.
      const redirect = onActiveChangeRef.current?.(msg.idx);
      if (typeof redirect === 'number' && redirect !== msg.idx) {
        syncedIdx.current = redirect;
        ch.postMessage({ type: 'goto', idx: redirect });
      }
    };
    return () => {
      ch.close();
      channelRef.current = null;
    };
  }, [slug]);

  // Editor → deck: filmstrip clicks / arrow keys navigate the preview.
  useEffect(() => {
    if (!booted) return;
    if (syncedIdx.current === activeIdx) return;
    syncedIdx.current = activeIdx;
    channelRef.current?.postMessage({ type: 'goto', idx: activeIdx });
  }, [activeIdx, booted]);

  function onFrameLoad(slot: number) {
    reloadGuardUntil.current = Date.now() + 600;

    // `load` fires for the proxy's plain-text 502 just as it does for a deck.
    // Treating that as a successful boot is what turned a slow cold start into
    // a permanently blank pane with nothing to click.
    const failure = frameFailure(frames.current[slot] ?? null);
    if (failure) {
      if (slot === frontRef.current) {
        // The frame the user is actually looking at now holds an error page.
        // This covers first boot AND a front buffer that dies later — Vite's
        // client calls location.reload() when its socket drops, which we
        // cannot patch out (location.reload is [Unforgeable]), so a frame that
        // was fine a moment ago can navigate itself onto a 502.
        setError(failure);
      } else if (swapPending.current || lateSwap.current) {
        // A back-buffer failure: abandon the swap and keep the good frame on
        // screen rather than cutting to an error over working content.
        swapPending.current = false;
        lateSwap.current = false;
        setUpdating(false);
      }
      return;
    }

    // First boot: nothing to swap, just reveal.
    if (!bootedRef.current) {
      if (slot !== frontRef.current) return;
      setBooted(true);
      setError(null);
      syncedIdx.current = null;
      assertPosition();
      // onLoad can beat the deck's channel subscription (its JS is still
      // booting), silently dropping the goto above. One delayed re-assert
      // covers that race; a goto for the slide the deck is already on is a
      // no-op there.
      later(assertPosition, 900);
      // The document can arrive intact and still never mount (see
      // frameMounted). Nothing else in the studio notices that, so check once.
      later(() => {
        if (frontRef.current !== slot) return;
        if (!frameMounted(frames.current[slot] ?? null)) {
          setError('The deck loaded but did not render. A slide may have a broken import.');
        }
      }, MOUNT_GRACE_MS);
      return;
    }

    // A back-buffer load lands here. Put it on the right slide BEFORE it is
    // shown — revealing first is what made the wrong slide flash past.
    if (slot === frontRef.current) return;
    if (!swapPending.current && !lateSwap.current) return;
    assertPosition();
    later(() => {
      if (!swapPending.current && !lateSwap.current) return;
      swapPending.current = false;
      lateSwap.current = false;
      const retiring = frontRef.current;
      setFront(slot);
      setUpdating(false);
      // Blank the retired frame so two live decks (two sets of videos) never
      // sit in memory together for longer than the swap itself.
      later(() => {
        const el = frames.current[retiring];
        if (el && frontRef.current !== retiring) el.src = 'about:blank';
      }, 120);
    }, 140);
  }

  return (
    <main className="preview-frame">
      {/* A failure must say so and offer a way out. Before this the pane was
          #000 on a #000 stage whether it was loading, broken, or fine — so a
          dead preview was indistinguishable from a slow one, and there was
          nothing to click either way. */}
      {error && (
        <div className="preview-frame__error" role="alert">
          <strong>Preview unavailable</strong>
          <p>{error}</p>
          <button type="button" className="btn" onClick={retry}>
            Try again
          </button>
        </div>
      )}
      {!booted && !error && (
        <div className="preview-frame__skeleton">
          <span>Starting preview…</span>
        </div>
      )}
      {booted && updating && (
        <div className="preview-frame__updating" role="status">
          Updating…
        </div>
      )}
      {SLOTS.map((slot) => (
        <iframe
          key={slot}
          ref={(el) => (frames.current[slot] = el)}
          title={`Preview: ${slug}`}
          className={'preview-frame__iframe' + (booted && slot === front ? '' : ' preview-frame__iframe--hidden')}
          onLoad={() => onFrameLoad(slot)}
          /* `fullscreen` is load-bearing: the deck shell renders its own
             fullscreen button + F key (shared/src/shell/Navigation.jsx), and
             without the permission requestFullscreen() rejects into a swallowed
             .catch() — the control looked broken for no visible reason. */
          allow="clipboard-write; fullscreen"
        />
      ))}
    </main>
  );
}
